import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Stack, Typography, CircularProgress, Button, FormControl, InputLabel, Select, MenuItem, Checkbox, ListItemText, OutlinedInput } from '@mui/material';
import { useGetTransformationJobsQuery } from '../../../../api/organizationSlice';
import ExecutionJobRow from './ExecutionJobRow';

const DEFAULT_STATUS_FILTERS = ['INGESTION_STARTED'];
const STATUS_OPTIONS = [
    { value: 'INGESTION_STARTED', label: 'Ingestion Started' },
    { value: 'TRANSFORMING', label: 'Transforming' },
    { value: 'TRANSFORMED', label: 'Transformed' },
    { value: 'INGESTION_FAILED', label: 'Ingestion Failed' },
    { value: 'TRANSFORM_FAILED', label: 'Transform Failed' },
    { value: 'ERROR', label: 'Error' },
];

function TransformationJobs({ organizationId }) {
    const [allExecutions, setAllExecutions] = useState([]);
    const [currentNextToken, setCurrentNextToken] = useState(null);
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [queryParams, setQueryParams] = useState({ organizationId, maxResults: 10 });
    const [selectedStatuses, setSelectedStatuses] = useState(DEFAULT_STATUS_FILTERS);
    
    const { data: transformationJobs, isLoading: isLoadingTransformationJobs, isFetching: isFetchingTransformationJobs } = useGetTransformationJobsQuery(queryParams);

    const availableStatusOptions = useMemo(() => {
        const discoveredStatuses = Array.from(
            new Set(
                allExecutions
                    .map((execution) => execution.details?.status)
                    .filter(Boolean)
            )
        );

        const knownStatusValues = STATUS_OPTIONS.map((option) => option.value);
        const additionalStatuses = discoveredStatuses.filter((status) => !knownStatusValues.includes(status));

        return [
            ...STATUS_OPTIONS,
            ...additionalStatuses.map((status) => ({ value: status, label: status }))
        ];
    }, [allExecutions]);

    const filteredExecutions = useMemo(() => {
        return allExecutions.filter((execution) => selectedStatuses.includes(execution.details?.status));
    }, [allExecutions, selectedStatuses]);

    const loadedJobCountText = `${filteredExecutions.length} of ${allExecutions.length} loaded jobs shown`;

    // Handle data loading
    useEffect(() => {
        if (!transformationJobs || isLoadingTransformationJobs || isFetchingTransformationJobs) {
            return;
        }

        if (transformationJobs.organizationId && transformationJobs.organizationId !== organizationId) {
            return;
        }

        if (isInitialLoad) {
            // Initial load
            setAllExecutions(transformationJobs.executions || []);
        } else {
            // Load more
            setAllExecutions(prev => [...prev, ...(transformationJobs.executions || [])]);
            setIsLoadingMore(false); // Clear loading more state
        }
        setCurrentNextToken(transformationJobs.nextToken);
    }, [transformationJobs, isLoadingTransformationJobs, isFetchingTransformationJobs, isInitialLoad, organizationId]);

    useEffect(() => {
        setAllExecutions([]);
        setCurrentNextToken(null);
        setIsInitialLoad(true);
        setIsLoadingMore(false);
        setQueryParams({ organizationId, maxResults: 10 });
        setSelectedStatuses(DEFAULT_STATUS_FILTERS);
    }, [organizationId]);

    const loadMore = useCallback(() => {
        if (currentNextToken && !isLoadingTransformationJobs && !isLoadingMore) {
            setIsInitialLoad(false);
            setIsLoadingMore(true);
            setQueryParams({
                organizationId,
                maxResults: 10,
                nextToken: currentNextToken
            });
        }
    }, [currentNextToken, isLoadingTransformationJobs, isLoadingMore, organizationId]);

    return (
        <Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ mt: 4 }}>
                <Typography variant='heavy' sx={{ fontSize: '18px' }}>
                    Transformation Jobs
                </Typography>

                <FormControl size="small" sx={{ minWidth: 240 }}>
                    <InputLabel id="transformation-job-status-filter-label">Filter by Status</InputLabel>
                    <Select
                        labelId="transformation-job-status-filter-label"
                        multiple
                        value={selectedStatuses}
                        label="Filter by Status"
                        onChange={(event) => {
                            const value = event.target.value;
                            setSelectedStatuses(typeof value === 'string' ? value.split(',') : value);
                        }}
                        input={<OutlinedInput label="Filter by Status" />}
                        renderValue={(selected) => {
                            const labels = selected.map((status) => {
                                const option = availableStatusOptions.find((entry) => entry.value === status);
                                return option?.label || status;
                            });

                            return labels.join(', ');
                        }}
                    >
                        {availableStatusOptions.map((status) => (
                            <MenuItem key={status.value} value={status.value}>
                                <Checkbox checked={selectedStatuses.indexOf(status.value) > -1} />
                                <ListItemText primary={status.label} />
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Stack>

            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Status filtering applies only to jobs already loaded in this list. Click Load More to fetch additional pages.
            </Typography>

            {!(isLoadingTransformationJobs && allExecutions.length > 0) && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {loadedJobCountText}
                </Typography>
            )}

            {isLoadingTransformationJobs && allExecutions.length === 0 ? (
                <Stack spacing={2} alignItems="center">
                    <CircularProgress />
                </Stack>
            ) : filteredExecutions.length > 0 ? (
                <Stack spacing={1} sx={{ mt: 2 }}>
                    {filteredExecutions.map((execution, index) => (
                        <ExecutionJobRow 
                            key={execution.executionArn}
                            execution={execution}
                        />
                    ))}
                </Stack>
            ) : allExecutions.length > 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    No loaded jobs match the selected statuses. Use Load More to check additional pages.
                </Typography>
            ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    No transformation jobs found
                </Typography>
            )}

            {currentNextToken && (
                <Stack direction="row" justifyContent="center" sx={{ mt: 2 }}>
                    <Button 
                        variant="outlined" 
                        onClick={loadMore} 
                        disabled={isLoadingTransformationJobs || isLoadingMore}
                        size="medium"
                    >
                        {isLoadingTransformationJobs || isLoadingMore ? 'Loading...' : 'Load More'}
                    </Button>
                </Stack>
            )}
        </Stack>
    );
}

export default TransformationJobs;